import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { RefreshCw, Target, ShoppingBag, Percent, TrendingUp, ArrowUp, ArrowDown, ArrowUpDown, Archive, Eye, Download, Trash2, LockKeyhole } from 'lucide-react';
import * as XLSX from 'xlsx';
import { pushWarehouseRecords, todayISODate } from '@/lib/warehouse-sync';
import { parseNumber, findColumnIndex } from '@/lib/channel-intel';
import { parseAnyDate, parseLocalDate, todayISO, firstOfMonthISO } from '@/lib/date-utils';
import { formatInt, pctTone, norm } from '@/lib/format-utils';
import { CHANNEL_OPTIONS, ALL_CHANNEL_VALUES, DISPATCH_SOURCES } from '@/lib/projections-config';
import DataTable from '@/components/DataTable';
import MultiSelect from '@/components/MultiSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Kept for backwards-compatibility within this file.
const parseDMY = parseAnyDate;

export default function ProjectionsPage() {
  const [projectionRows, setProjectionRows] = useState([]);
  const [projectionSheet, setProjectionSheet] = useState('');
  // Per-month projection data keyed by 'YYYY-MM'. Each entry:
  //   { rows: [...], sheet: 'Sep\'26 Projection', scale: 0..1 }
  // `scale` = days-of-that-month-inside-range / days-in-month, so a partial
  // window pro-rates the whole-month projection instead of showing the
  // whole-month figure regardless of range. Range spanning multiple months
  // sums each month's contribution.
  const [projectionByMonth, setProjectionByMonth] = useState({});
  // Ordered list of sheet names actually loaded (for the source label).
  const [projectionSheets, setProjectionSheets] = useState([]);
  const [mtdSalesRows, setMtdSalesRows] = useState([]);
  // Each entry: { rows: [...], tab: 'sheet name' }. Keyed by dispatch source id.
  const [dispatchData, setDispatchData] = useState({});
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(firstOfMonthISO());
  const [endDate, setEndDate] = useState(todayISO());

  // Filters — [] means ALL (consistent with Stock Allocation)
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);

  const effectiveChannels = selectedChannels.length === 0 ? ALL_CHANNEL_VALUES : selectedChannels;

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Every YYYY-MM string touched by [start, end], inclusive, in order.
  const monthsInRange = (startISO, endISO) => {
    if (!startISO || !endISO) return [];
    const s = parseLocalDate(startISO); const e = parseLocalDate(endISO);
    if (!s || !e || e < s) return [];
    const out = [];
    let y = s.getFullYear(); let m = s.getMonth();
    while (y < e.getFullYear() || (y === e.getFullYear() && m <= e.getMonth())) {
      out.push(`${y}-${String(m + 1).padStart(2, '0')}`);
      m += 1; if (m > 11) { m = 0; y += 1; }
    }
    return out;
  };

  // Fraction of a given YYYY-MM month that falls inside [start, end].
  // 1.0 when the month is fully inside the range, 0 when disjoint.
  const monthScale = (ym, startISO, endISO) => {
    if (!ym || !startISO || !endISO) return 0;
    const [y, m] = ym.split('-').map(Number);
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd   = new Date(y, m, 0); // last day of that month
    const rStart = parseLocalDate(startISO);
    const rEnd   = parseLocalDate(endISO);
    if (!rStart || !rEnd) return 0;
    const lo = rStart > monthStart ? rStart : monthStart;
    const hi = rEnd   < monthEnd   ? rEnd   : monthEnd;
    if (hi < lo) return 0;
    const daysInMonth = monthEnd.getDate();
    const daysInRange = Math.round((hi.getTime() - lo.getTime()) / 86400000) + 1;
    return Math.max(0, Math.min(1, daysInRange / daysInMonth));
  };

  // Re-fetch every monthly projection sheet touched by [startDate, endDate]
  // and record its scale factor. Ops picking "1–31 May 2026" pulls May'26
  // Projection × 1.0; "15 May – 15 Jun" pulls May × 17/31 + Jun × 15/30;
  // ranges outside any known sheet fall through to the fallback and are
  // scaled to 0 so no phantom units leak in.
  useEffect(() => {
    const months = monthsInRange(startDate, endDate);
    if (!months.length) {
      setProjectionByMonth({}); setProjectionSheets([]);
      setProjectionRows([]);   setProjectionSheet('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ts = Date.now();
        const headers = {
          Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        };
        const results = await Promise.all(months.map(async (ym) => {
          try {
            const res = await axios.get(`${API}/sheets/projection`, {
              headers, params: { _t: ts, month: ym },
            });
            return { ym, rows: res.data.rows || [], sheet: res.data.sheet || '',
                     fallback: !!res.data.fallback };
          } catch (e) {
            return { ym, rows: [], sheet: '', fallback: false, err: e };
          }
        }));
        if (cancelled) return;
        const map = {};
        const sheets = [];
        let fallbackNote = null;
        results.forEach(({ ym, rows, sheet, fallback }) => {
          map[ym] = { rows, sheet, scale: monthScale(ym, startDate, endDate) };
          if (sheet && !sheets.includes(sheet)) sheets.push(sheet);
          if (fallback && !fallbackNote) fallbackNote = { ym, sheet };
        });
        setProjectionByMonth(map);
        setProjectionSheets(sheets);
        // Primary (start-month) projection powers legacy consumers — the
        // brand/SKU filter dropdowns, the title→projection resolver, etc.
        const primary = map[months[0]];
        if (primary) {
          setProjectionRows(primary.rows);
          setProjectionSheet(primary.sheet);
        }
        if (fallbackNote) {
          toast(`No projection sheet for ${fallbackNote.ym} yet — using ${fallbackNote.sheet}`, { icon: '⚠️' });
        }
      } catch (e) {
        if (!cancelled) toast.error(`Failed to load projection sheet(s) for ${months.join(', ')}`);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    // Wipe previous datasets so any stale memo/render can't show old numbers
    // while the fresh fetch is in flight.
    setProjectionRows([]);
    setMtdSalesRows([]);
    setDispatchData({});
    try {
      // Cache-bust: timestamp every request + no-store headers so neither the
      // browser nor any intermediate proxy can serve a stale response.
      const ts = Date.now();
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      };
      const cfg = { headers, params: { _t: ts } };
      // The monthly projection sheet is dynamic — pass the start date's month
      // so future months (Jul'26, Aug'26, ...) work without code changes.
      const projCfg = { headers, params: { _t: ts, month: (startDate || '').slice(0, 7) } };
      const dispatchKeys = ['blinkit', 'fbf', 'fba', 'offline', 'fk_mins', 'zepto', 'win', 'az_now'];
      const dispatchPromises = dispatchKeys.map(k =>
        k === 'blinkit'
          ? axios.get(`${API}/sheets/blinkit-planned`, cfg)
          : axios.get(`${API}/sheets/dispatch/${k}`, cfg)
      );
      const [proj, mtd, mapping, ...dispatchResults] = await Promise.all([
        axios.get(`${API}/sheets/projection`, projCfg),
        axios.get(`${API}/sheets/mtd-sales`, cfg),
        axios.get(`${API}/sheets/dispatch/mappings`, cfg),
        ...dispatchPromises,
      ]);
      setProjectionRows(proj.data.rows || []);
      setProjectionSheet(proj.data.sheet || '');
      setMtdSalesRows(mtd.data.rows || []);
      const dispatch = { mappings: { rows: mapping.data.rows || [] } };
      dispatchKeys.forEach((k, i) => {
        dispatch[k] = { rows: dispatchResults[i].data.rows || [] };
      });
      setDispatchData(dispatch);
      toast.success('Projections, dispatched & sales loaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ============ Mappings: build any-key (ASIN / TITLE / FSN / W.in SKU) → norm(TITLE) ============
  // Case-insensitive + trimmed. norm(TITLE) is the canonical row key that aligns with
  // May'26 Projection's SKU NAME column.
  //
  // The Mappings tab can carry duplicate rows for the same identifier (e.g. ORB-BLK-1 appears
  // twice — once mapped to "Offline Retail Bag Black", once to "Carry Bags - Black"). We
  // prefer the variant whose TITLE actually exists in the projection so dispatched units land
  // on the correct row instead of getting marked "Unmapped".
  const projectionTitleSet = useMemo(() => {
    if (!projectionRows || projectionRows.length < 2) return new Set();
    const hdr = projectionRows[0];
    const skuIdx = findColumnIndex(hdr, ['sku name', 'sku', 'product name']);
    if (skuIdx === -1) return new Set();
    const set = new Set();
    projectionRows.slice(1).forEach(r => {
      const v = norm(r[skuIdx]);
      if (v) set.add(v);
    });
    return set;
  }, [projectionRows]);

  const titleByKey = useMemo(() => {
    const rows = dispatchData.mappings?.rows;
    if (!rows || rows.length < 2) return {};
    const hdr = rows[0];
    const idx = {
      asin:    findColumnIndex(hdr, ['asin']),
      fsn:     findColumnIndex(hdr, ['fsn']),
      winSku:  findColumnIndex(hdr, ['w.in sku', 'win sku']),
      title:   findColumnIndex(hdr, ['title']),
    };
    const out = {};
    // Track whether the existing mapping for a key is "projection-confirmed".
    // This lets a later projection-confirmed row overwrite an earlier non-confirmed one,
    // but a non-confirmed row will NOT overwrite a confirmed one.
    const confirmed = {};
    const setMapping = (rawKey, title) => {
      if (!rawKey || !title) return;
      const k = norm(rawKey);
      const t = norm(title);
      if (!k || !t) return;
      const isConfirmed = projectionTitleSet.has(t);
      if (!(k in out) || (isConfirmed && !confirmed[k])) {
        out[k] = t;
        confirmed[k] = isConfirmed;
      }
    };

    rows.slice(1).forEach(r => {
      const title = idx.title !== -1 ? (r[idx.title] || '').toString().trim() : '';
      if (!title) return;
      // Identity (TITLE → TITLE) + each identifier-style column → TITLE.
      setMapping(title, title);
      if (idx.asin   !== -1) setMapping(r[idx.asin],   title);
      if (idx.fsn    !== -1) setMapping(r[idx.fsn],    title);
      if (idx.winSku !== -1) setMapping(r[idx.winSku], title);
    });
    return out;
  }, [dispatchData, projectionTitleSet]);

  // Resolve a sheet identifier to canonical title.
  // 1) Exact normalized match via Mappings (titleByKey)
  // 2) Direct projection-title match (when the id already IS the title — FBA TITLE,
  //    FK Mins Product Name, Zepto Product Name, Blinkit Product Name, etc.)
  // 3) Fallback to normalised id — kept so we don't drop dispatched units; the row
  //    surfaces as "Unmapped" in the table and a console warning is logged.
  // Returns the canonical key (always a string).
  const resolveTitle = (val) => {
    const k = norm(val);
    if (!k) return '';
    if (titleByKey[k]) return titleByKey[k];
    if (projectionTitleSet.has(k)) return k;
    return k;
  };

  // ============ Build projection map: SKU NAME (normalised) → { sku, brand, projected } ============
  // Projection is a WHOLE-MONTH figure (not date-windowed). It sums whichever channel
  // columns are currently selected (or all of them when no channel filter is applied).
  const projectionBySku = useMemo(() => {
    if (!projectionRows || projectionRows.length < 2) return {};
    const headers = projectionRows[0];
    const skuIdx = findColumnIndex(headers, ['sku name', 'sku', 'product name']);
    const brandIdx = findColumnIndex(headers, ['brand']);
    const asinIdx = findColumnIndex(headers, ['asin']);
    if (skuIdx === -1) {
      console.error('[projections] Required SKU column missing in "May\'26 Projection"');
      return {};
    }
    // Resolve column index for each effective channel's projection header.
    const projColIdx = {};
    effectiveChannels.forEach(ch => {
      const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
      if (!opt) return;
      const idx = findColumnIndex(headers, [opt.projCol]);
      if (idx !== -1) projColIdx[ch] = idx;
      else console.warn(`[projections] Column "${opt.projCol}" missing in "${projectionSheet || 'projection'}" sheet`);
    });

    const out = {};
    projectionRows.slice(1).forEach(row => {
      const sku = (row[skuIdx] || '').toString().trim();
      if (!sku) return;
      let projected = 0;
      Object.values(projColIdx).forEach(ci => { projected += parseNumber(row[ci]); });
      out[norm(sku)] = {
        sku,
        asin: asinIdx !== -1 ? (row[asinIdx] || '').toString().trim() : '',
        brand: (row[brandIdx] || '').toString().trim(),
        projected,
      };
    });
    return out;
  }, [projectionRows, effectiveChannels]);

  // Determine which dispatch source keys to read (based on channel filter)
  const activeDispatchSources = useMemo(() => {
    const keys = new Set();
    effectiveChannels.forEach(ch => {
      const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
      (opt?.dispatch || []).forEach(k => keys.add(k));
    });
    return [...keys];
  }, [effectiveChannels]);

  // Whether ANY synced dispatch source is in scope for the current channel filter.
  // Used for UX hinting ("No dispatched source for selected channel(s)" etc.).
  const anyDispatchInScope = activeDispatchSources.length > 0;

  // Channels in scope that DON'T currently have a dispatch source synced.
  const pendingDispatchChannels = useMemo(() => {
    return effectiveChannels.filter(ch => {
      const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
      return !(opt?.dispatch || []).length;
    });
  }, [effectiveChannels]);

  // ============ UNIFIED FACTS — single source of truth ============================
  // One walk over projection + dispatch + sales sheets, producing per-(SKU, channel)
  // facts that ALL downstream views (cards / channel summary / SKU table) consume.
  //
  // Rules:
  //   • Projection is whole-month from May'26/Jun'26 Projection sheet, summed only
  //     over `effectiveChannels` per SKU. Brand/SKU filter applied at SKU level.
  //   • Dispatched is date-range-filtered per dispatch source, mapped to TITLE via
  //     Mappings, channel-tagged by which CHANNEL_OPTIONS source it came from.
  //   • Sales is date-range-filtered from MTD Sales 1, multi-SKU FULL attribution
  //     (each row's per-channel units added to EACH listed SKU).
  //   • Brand/SKU filter on dispatch + sales: rows that resolve to a projection
  //     SKU which fails the filter are excluded; rows that resolve to an UNKNOWN
  //     SKU are excluded only when a brand/sku filter is active (matches prior
  //     "Unmapped row" behaviour).
  //
  // Output:
  //   • byChannel[ch] → { projection, dispatched, sales }
  //   • bySku[tkey]   → { sku, asin, brand, projected, dispatched, sales, unmapped }
  //   • unmappedBySku — same shape, channel union of all unresolved IDs (only
  //                      populated when no brand/sku filter active).
  //   • cardTotals + channelTotals + skuTotals — used for the validation log.
  const unifiedFacts = useMemo(() => {
    const start = startDate ? parseLocalDate(startDate) : null;
    const end   = endDate   ? parseLocalDate(endDate)   : null;
    if (end) end.setHours(23, 59, 59, 999);
    const inRange = (d) => {
      if (!d) return false;
      if (start && d < start) return false;
      if (end   && d > end)   return false;
      return true;
    };

    const filterActive = selectedBrands.length > 0 || selectedSkus.length > 0;

    // Counters for the debug log — exposed via `counts` so the UI can show how
    // many records actually flowed through each pipeline pass.
    const counts = {
      projectionTotalRows: 0,
      projectionRowsKept: 0,
      dispatchTotalRows: 0,
      dispatchRowsKept: 0,
      dispatchRowsOutOfRange: 0,
      dispatchRowsFilteredOut: 0,
      salesTotalRows: 0,
      salesRowsKept: 0,
      salesRowsOutOfRange: 0,
      salesRowsFilteredOut: 0,
    };

    // ---- Pass 1: Projection (whole-month per sheet, scaled to range) ---------
    // Each month present in [startDate, endDate] contributes its projection
    // × (days-of-month-in-range / days-in-month). Ranges narrower than a full
    // month therefore return a pro-rated projection; ranges spanning several
    // months sum each month's pro-rated contribution. Ranges disjoint from
    // every known projection sheet yield 0 and the UI naturally shows blanks.
    const skuMeta = {};
    const projByKey = {}; // `${tkey}|${ch}` -> scaled projection units

    Object.entries(projectionByMonth || {}).forEach(([ym, entry]) => {
      const rows  = entry?.rows;
      const scale = entry?.scale || 0;
      if (!rows || rows.length < 2 || !scale) return;
      const phdr = rows[0];
      const pskuIdx   = findColumnIndex(phdr, ['sku name', 'sku', 'product name']);
      const pbrandIdx = findColumnIndex(phdr, ['brand']);
      const pasinIdx  = findColumnIndex(phdr, ['asin']);
      if (pskuIdx === -1) {
        console.error(`[projections] Required SKU column missing in "${entry.sheet || ym}" sheet`);
        return;
      }
      const projColIdx = {};
      effectiveChannels.forEach(ch => {
        const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
        if (!opt) return;
        const ci = findColumnIndex(phdr, [opt.projCol]);
        if (ci !== -1) projColIdx[ch] = ci;
        else console.warn(`[projections] Column "${opt.projCol}" missing in "${entry.sheet || ym}" sheet`);
      });

      rows.slice(1).forEach(row => {
        counts.projectionTotalRows += 1;
        const sku = (row[pskuIdx] || '').toString().trim();
        if (!sku) return;
        const tkey  = norm(sku);
        const brand = pbrandIdx !== -1 ? (row[pbrandIdx] || '').toString().trim() : '';
        const asin  = pasinIdx  !== -1 ? (row[pasinIdx]  || '').toString().trim() : '';
        const passes =
          (selectedBrands.length === 0 || selectedBrands.includes(brand)) &&
          (selectedSkus.length   === 0 || selectedSkus.includes(sku));
        // First encounter wins for meta so dispatch/sales attribution stays
        // stable across months; subsequent months just accumulate `projByKey`.
        if (!skuMeta[tkey]) skuMeta[tkey] = { sku, asin, brand, passes };
        if (!skuMeta[tkey].passes) return;
        counts.projectionRowsKept += 1;
        Object.entries(projColIdx).forEach(([ch, ci]) => {
          const v = parseNumber(row[ci]);
          if (!v) return;
          const k = `${tkey}|${ch}`;
          projByKey[k] = (projByKey[k] || 0) + v * scale;
        });
      });
    });

    // ---- Pass 2: Dispatched per (title, channel), date-windowed --------------
    const dispByKey      = {}; // resolved -> projection SKU
    const unmappedDisp   = {}; // resolved -> NOT in projection (only when filterActive==false)
    const unresolvedDbg  = {}; // diagnostics: per-tab Set of raw ids that didn't match a projection title

    effectiveChannels.forEach(ch => {
      const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
      const keys = opt?.dispatch || [];
      keys.forEach(key => {
        const src = DISPATCH_SOURCES[key];
        const rows = dispatchData[key]?.rows;
        if (!src || !rows || rows.length < 2) return;
        const hdr = rows[0];
        const asArr = (v) => Array.isArray(v) ? v : [v];
        const dIdx = findColumnIndex(hdr, asArr(src.dateCol));
        const iIdx = findColumnIndex(hdr, asArr(src.idCol));
        const uIdx = findColumnIndex(hdr, asArr(src.unitsCol));
        if (dIdx === -1 || iIdx === -1 || uIdx === -1) {
          console.warn(`[projections] Columns missing in "${src.tab}" → date:${dIdx} id:${iIdx} units:${uIdx}`);
          return;
        }
        rows.slice(1).forEach(row => {
          counts.dispatchTotalRows += 1;
          const d = parseAnyDate(row[dIdx]);
          if (!inRange(d)) { counts.dispatchRowsOutOfRange += 1; return; }
          const id = (row[iIdx] || '').toString().trim();
          if (!id) return;
          const units = parseNumber(row[uIdx]);
          if (!units) return;
          const tkey = resolveTitle(id);
          const meta = skuMeta[tkey];
          if (meta) {
            if (!meta.passes) { counts.dispatchRowsFilteredOut += 1; return; }
            counts.dispatchRowsKept += 1;
            const k = `${tkey}|${ch}`;
            dispByKey[k] = (dispByKey[k] || 0) + units;
          } else {
            // Unresolved-to-projection: track for diagnostics; only include in
            // totals when brand/sku filters are NOT active (parity with prior
            // tableRows behaviour, prevents phantom rows under active filters).
            (unresolvedDbg[src.tab] ||= new Set()).add(id);
            if (filterActive) { counts.dispatchRowsFilteredOut += 1; return; }
            counts.dispatchRowsKept += 1;
            const k = `${tkey}|${ch}`;
            unmappedDisp[k] = (unmappedDisp[k] || 0) + units;
          }
        });
      });
    });
    Object.entries(unresolvedDbg).forEach(([tab, set]) => {
      const sample = [...set].slice(0, 5).join(', ');
      console.warn(`[projections] ${set.size} identifier(s) in "${tab}" did not match any projection SKU NAME via Mappings. Sample: ${sample}`);
    });

    // ---- Pass 3: Sales per (title, channel), date-windowed -------------------
    // MTD Sales 1 has per-channel unit columns + up to 4 SKUs per row.
    // Multi-SKU FULL ATTRIBUTION: each row's per-channel units are added to
    // EVERY contributing SKU. This is the rule the live page has always used.
    const salesByKey    = {};
    const unmappedSales = {};

    if (mtdSalesRows && mtdSalesRows.length > 1) {
      const mhdr = mtdSalesRows[0];
      const dIdx = findColumnIndex(mhdr, ['date']);
      const bIdx = findColumnIndex(mhdr, ['brand']);
      const titleIdx = findColumnIndex(mhdr, ['title']);
      const skuIdxs = [
        findColumnIndex(mhdr, ['sku 1']),
        findColumnIndex(mhdr, ['sku 2']),
        findColumnIndex(mhdr, ['sku 3']),
        findColumnIndex(mhdr, ['sku 4']),
      ].filter(i => i !== -1);
      const chColIdx = {};
      effectiveChannels.forEach(ch => {
        const opt = CHANNEL_OPTIONS.find(o => o.value === ch);
        if (!opt) return;
        const ci = findColumnIndex(mhdr, [opt.unitsCol]);
        if (ci !== -1) chColIdx[ch] = ci;
      });

      if (dIdx === -1 || skuIdxs.length === 0) {
        console.error('[projections] Required columns missing in "MTD Sales 1"');
      } else {
        mtdSalesRows.slice(1).forEach(row => {
          counts.salesTotalRows += 1;
          const d = parseAnyDate(row[dIdx]);
          if (!inRange(d)) { counts.salesRowsOutOfRange += 1; return; }
          // Brand filter — once per row
          if (selectedBrands.length > 0) {
            const brand = bIdx !== -1 ? (row[bIdx] || '').toString().trim() : '';
            if (!selectedBrands.includes(brand)) { counts.salesRowsFilteredOut += 1; return; }
          }
          // Collect distinct SKUs in this row (with TITLE fallback)
          const skus = [];
          const seen = new Set();
          skuIdxs.forEach(si => {
            const v = (row[si] || '').toString().trim();
            if (!v) return;
            const k = norm(v);
            if (seen.has(k)) return;
            seen.add(k);
            skus.push(v);
          });
          if (skus.length === 0 && titleIdx !== -1) {
            const t = (row[titleIdx] || '').toString().trim();
            if (t) skus.push(t);
          }
          if (skus.length === 0) return;

          const skusContrib = selectedSkus.length === 0
            ? skus
            : skus.filter(s => selectedSkus.includes(s));
          if (skusContrib.length === 0) { counts.salesRowsFilteredOut += 1; return; }
          counts.salesRowsKept += 1;

          skusContrib.forEach(skuVal => {
            const tkey = resolveTitle(skuVal);
            const meta = skuMeta[tkey];
            const known = !!meta;
            if (known && !meta.passes) return;
            if (!known && filterActive) return;
            Object.entries(chColIdx).forEach(([ch, ci]) => {
              const v = parseNumber(row[ci]);
              if (!v) return;
              const k = `${tkey}|${ch}`;
              if (known) salesByKey[k]    = (salesByKey[k]    || 0) + v;
              else       unmappedSales[k] = (unmappedSales[k] || 0) + v;
            });
          });
        });
      }
    }

    // ---- Aggregate to byChannel + bySku + unmappedBySku ----------------------
    const byChannel = {};
    effectiveChannels.forEach(ch => {
      byChannel[ch] = { projection: 0, dispatched: 0, sales: 0 };
    });

    const bySku = {};
    Object.entries(skuMeta).forEach(([tkey, m]) => {
      if (!m.passes) return;
      const r = { sku: m.sku, asin: m.asin, brand: m.brand,
                  projected: 0, dispatched: 0, sales: 0, unmapped: false };
      effectiveChannels.forEach(ch => {
        const k = `${tkey}|${ch}`;
        const p = projByKey[k]  || 0;
        const d = dispByKey[k]  || 0;
        const s = salesByKey[k] || 0;
        r.projected  += p;
        r.dispatched += d;
        r.sales      += s;
        byChannel[ch].projection += p;
        byChannel[ch].dispatched += d;
        byChannel[ch].sales      += s;
      });
      bySku[tkey] = r;
    });

    const unmappedBySku = {};
    const allUnmappedKeys = new Set([
      ...Object.keys(unmappedDisp),
      ...Object.keys(unmappedSales),
    ]);
    allUnmappedKeys.forEach(k => {
      const [tkey, ch] = k.split('|');
      const d = unmappedDisp[k]  || 0;
      const s = unmappedSales[k] || 0;
      if (!unmappedBySku[tkey]) {
        unmappedBySku[tkey] = {
          sku: tkey, asin: '', brand: '',
          projected: 0, dispatched: 0, sales: 0, unmapped: true,
        };
      }
      unmappedBySku[tkey].dispatched += d;
      unmappedBySku[tkey].sales      += s;
      if (byChannel[ch]) {
        byChannel[ch].dispatched += d;
        byChannel[ch].sales      += s;
      }
    });

    // ---- Final shapes consumed by the UI -------------------------------------
    const tableRows = [
      ...Object.values(bySku),
      ...Object.values(unmappedBySku),
    ].map(r => ({
      ...r,
      pct:      r.projected > 0 ? (r.dispatched / r.projected) * 100 : null,
      salesPct: r.projected > 0 ? (r.sales      / r.projected) * 100 : null,
    })).sort((a, b) => {
      if (a.unmapped !== b.unmapped) return a.unmapped ? 1 : -1;
      const av = a.pct ?? -1;
      const bv = b.pct ?? -1;
      return bv - av;
    });

    const channelRows = effectiveChannels.map(ch => {
      const c = byChannel[ch];
      return {
        channel: ch,
        projection: c.projection,
        dispatched: c.dispatched,
        sales:      c.sales,
        dispPct:    c.projection > 0 ? (c.dispatched / c.projection) * 100 : null,
        salePct:    c.projection > 0 ? (c.sales      / c.projection) * 100 : null,
        dispSalesPct: c.sales > 0 ? (c.dispatched / c.sales) * 100 : null,
      };
    });

    // ---- Validation: cards / channels / SKUs must all agree ------------------
    const cardTotals = tableRows.reduce((acc, r) => ({
      projected:  acc.projected  + r.projected,
      dispatched: acc.dispatched + r.dispatched,
      sales:      acc.sales      + r.sales,
    }), { projected: 0, dispatched: 0, sales: 0 });
    const channelTotals = channelRows.reduce((acc, r) => ({
      projected:  acc.projected  + r.projection,
      dispatched: acc.dispatched + r.dispatched,
      sales:      acc.sales      + r.sales,
    }), { projected: 0, dispatched: 0, sales: 0 });
    const mismatch = {
      projected:  cardTotals.projected  !== channelTotals.projected,
      dispatched: cardTotals.dispatched !== channelTotals.dispatched,
      sales:      cardTotals.sales      !== channelTotals.sales,
    };
    /* eslint-disable no-console */
    console.log('[projections] dataset', {
      dateRange: { startDate, endDate },
      channels: effectiveChannels,
      filters: { brands: selectedBrands, skus: selectedSkus },
      cardTotals,
      channelTotals,
      mismatch,
      skuCount: tableRows.length,
      channelCount: channelRows.length,
      records: counts,
    });
    if (mismatch.projected || mismatch.dispatched || mismatch.sales) {
      console.warn('[projections] MISMATCH — cards vs channel summary', { cardTotals, channelTotals });
    }
    /* eslint-enable no-console */

    return { tableRows, channelRows, cardTotals, byChannel, bySku, counts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectionRows, projectionByMonth, mtdSalesRows, dispatchData, effectiveChannels,
      selectedBrands, selectedSkus, startDate, endDate, titleByKey]);

  // ============ Per-channel summary — derived from unified facts ============
  // No separate computation path — the same data the cards and SKU table use.
  const channelSummaryLive = unifiedFacts.channelRows;

  // Ideal Sales % = days in selected range / total days in the month of startDate.
  //   • Pacing benchmark for the chosen window (e.g. 1–18 May → 18/31 ≈ 58.1%).
  //   • Anchored to the month containing `startDate`; cross-month ranges still use that
  //     month's day-count so the metric stays interpretable.
  const idealSalesPct = useMemo(() => {
    const s = parseLocalDate(startDate);
    const e = parseLocalDate(endDate);
    if (!s || !e || e < s) return 0;
    const days = Math.round((e - s) / 86400000) + 1; // inclusive
    const totalDaysInMonth = new Date(s.getFullYear(), s.getMonth() + 1, 0).getDate();
    return (days / totalDaysInMonth) * 100;
  }, [startDate, endDate]);
  // Days info for the subtitle on Table 1.
  const idealDaysInfo = useMemo(() => {
    const s = parseLocalDate(startDate);
    const e = parseLocalDate(endDate);
    if (!s || !e || e < s) return { days: 0, total: 0 };
    const days = Math.round((e - s) / 86400000) + 1;
    const total = new Date(s.getFullYear(), s.getMonth() + 1, 0).getDate();
    return { days, total };
  }, [startDate, endDate]);

  // ============ Warehouse-served historical mode ============
  // When the user picks a range whose end date is strictly before today, we
  // route the entire page through MongoDB instead of Google Sheets:
  //   • channelSummary  ← warehouse rows with sku === '__TOTAL__'
  //   • tableRows       ← warehouse rows with channel === '__TOTAL__'
  // Records are state-at-date snapshots, so the "right" historical value is
  // the freshest record ≤ end_date for each group key. The page's existing
  // KPI cards, sort, filter and Excel export logic see a normal-looking
  // `channelSummary` / `tableRows` and need no further changes.
  const todayStr = todayISODate();
  const isPastRange = useMemo(() => {
    if (!endDate) return false;
    return endDate < todayStr;
  }, [endDate, todayStr]);

  const [warehouseRows, setWarehouseRows] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseUsed, setWarehouseUsed] = useState(false);

  useEffect(() => {
    if (!isPastRange) { setWarehouseRows([]); setWarehouseUsed(false); return; }
    let cancelled = false;
    (async () => {
      setWarehouseLoading(true);
      try {
        const res = await axios.get(`${API}/warehouse/daily`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('radar_session_token')}` },
          params: { start_date: startDate, end_date: endDate, source: 'consumption', limit: 5000 },
        });
        if (!cancelled) {
          setWarehouseRows(res.data?.rows || []);
          setWarehouseUsed(true);
        }
      } catch (e) {
        if (!cancelled) {
          setWarehouseRows([]);
          setWarehouseUsed(false);
          // Silent — the live fallback (if available) still renders the page.
        }
      } finally {
        if (!cancelled) setWarehouseLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPastRange, startDate, endDate]);

  // ============ Historical (warehouse) view — single-anchor-date snapshot ============
  // Both the channel summary and the SKU table must reflect the SAME point-in-time
  // snapshot, otherwise cards (which sum per-SKU rows) drift from the channel
  // summary (which sums per-channel rows) whenever the two grains were synced on
  // different dates within the range.
  //
  // The anchor = the latest snapshot date in the selected range that has at least
  // one row of EITHER grain. We filter warehouseRows to that date only, then split
  // into per-channel (sku='__TOTAL__') and per-SKU (channel='__TOTAL__') views.
  // The user is told via the banner which date was chosen.
  const warehouseAnchorDate = useMemo(() => {
    if (!warehouseUsed || warehouseRows.length === 0) return null;
    let max = '';
    warehouseRows.forEach(r => { if (r.date && r.date > max) max = r.date; });
    return max || null;
  }, [warehouseUsed, warehouseRows]);

  const warehouseAnchoredRows = useMemo(() => {
    if (!warehouseAnchorDate) return [];
    return warehouseRows.filter(r => r.date === warehouseAnchorDate);
  }, [warehouseRows, warehouseAnchorDate]);

  const channelSummaryHistorical = useMemo(() => {
    if (!warehouseUsed) return null;
    const channelRows = warehouseAnchoredRows.filter(r => r.sku === '__TOTAL__');
    if (channelRows.length === 0) return [];
    const out = [];
    // One row per channel — duplicates within the anchor date are summed (defensive).
    const byCh = new Map();
    channelRows.forEach(r => {
      const ch = r.channel;
      const slot = byCh.get(ch) || { projection: 0, dispatched: 0, sales: 0 };
      slot.projection  += Number(r.projected_units)  || 0;
      slot.dispatched  += Number(r.dispatched_units) || 0;
      slot.sales       += Number(r.sales_units)      || 0;
      byCh.set(ch, slot);
    });
    byCh.forEach((agg, channel) => {
      out.push({
        channel,
        projection: agg.projection,
        dispatched: agg.dispatched,
        sales:      agg.sales,
        dispPct: agg.projection > 0 ? (agg.dispatched / agg.projection) * 100 : null,
        salePct: agg.projection > 0 ? (agg.sales      / agg.projection) * 100 : null,
        dispSalesPct: agg.sales > 0 ? (agg.dispatched / agg.sales) * 100 : null,
      });
    });
    // Apply visible-channel filter to match the live path's behaviour.
    return out.filter(r => {
      const opt = CHANNEL_OPTIONS.find(o => o.label === r.channel || o.value === r.channel);
      return effectiveChannels.includes(opt ? opt.value : r.channel);
    });
  }, [warehouseUsed, warehouseAnchoredRows, effectiveChannels]);

  // ====== Live vs Historical resolution =====================================
  // Live (Google Sheets via unifiedFacts) is the PRIMARY source for any date
  // range — past, current, or future. The MongoDB warehouse is only used when
  // the live aggregation comes back genuinely empty (e.g., the Google Sheets
  // tab for that month no longer exists, or auth is broken). This guarantees
  // selecting a past range never collapses the visible record count down to
  // the (tiny) saved snapshot — we keep showing the full date-range aggregate.
  const liveHasData = (
    unifiedFacts.cardTotals.projected  > 0 ||
    unifiedFacts.cardTotals.dispatched > 0 ||
    unifiedFacts.cardTotals.sales      > 0
  );
  const useHistoricalFallback = !liveHasData && warehouseUsed && warehouseRows.length > 0;

  // Public-facing per-channel computed.
  const channelSummary = useHistoricalFallback
    ? (channelSummaryHistorical ?? channelSummaryLive)
    : channelSummaryLive;


  // Augment channel summary rows with ideal/variance for Table 1.
  const channelSummaryAugmented = useMemo(
    () => channelSummary.map(r => ({
      ...r,
      idealPct: idealSalesPct,
      variancePct: r.salePct == null ? null : r.salePct - idealSalesPct,
    })),
    [channelSummary, idealSalesPct]
  );

  // ============ Distinct brand / SKU options (drawn from projections) ============
  const brandOptions = useMemo(() => {
    const set = new Set();
    Object.values(projectionBySku).forEach(p => { if (p.brand) set.add(p.brand); });
    return [...set].sort().map(b => ({ value: b, label: b }));
  }, [projectionBySku]);

  const skuOptions = useMemo(() => {
    const set = new Set();
    Object.values(projectionBySku).forEach(p => { if (p.sku) set.add(p.sku); });
    return [...set].sort().map(s => ({ value: s, label: s }));
  }, [projectionBySku]);

  // ============ SKU table — derived from unified facts ============
  // Same dataset as channelSummaryLive + KPI cards. Single source of truth.
  const tableRowsLive = unifiedFacts.tableRows;

  // Build historical per-SKU table from the SAME anchor-date snapshot used by
  // the channel summary above, so cards / channels / SKUs all match.
  const tableRowsHistorical = useMemo(() => {
    if (!warehouseUsed) return null;
    const skuRows = warehouseAnchoredRows.filter(r => r.channel === '__TOTAL__');
    if (skuRows.length === 0) return [];
    // One row per SKU — defensive sum across duplicates.
    const bySku = new Map();
    skuRows.forEach(r => {
      const meta = bySku.get(r.sku) || { asin: r.asin || '', brand: r.brand || '', projected: 0, dispatched: 0, sales: 0 };
      meta.projected  += Number(r.projected_units)  || 0;
      meta.dispatched += Number(r.dispatched_units) || 0;
      meta.sales      += Number(r.sales_units)      || 0;
      bySku.set(r.sku, meta);
    });
    const out = [];
    bySku.forEach((m, sku) => {
      if (selectedBrands.length > 0 && !selectedBrands.includes(m.brand)) return;
      if (selectedSkus.length   > 0 && !selectedSkus.includes(sku)) return;
      out.push({
        sku,
        asin:  m.asin,
        brand: m.brand,
        projected:  m.projected,
        dispatched: m.dispatched,
        sales:      m.sales,
        pct:      m.projected > 0 ? (m.dispatched / m.projected) * 100 : null,
        salesPct: m.projected > 0 ? (m.sales      / m.projected) * 100 : null,
        unmapped: false,
      });
    });
    return out;
  }, [warehouseUsed, warehouseAnchoredRows, selectedBrands, selectedSkus]);

  // Public-facing per-SKU computed — live is primary, warehouse is fallback only
  // when live is genuinely empty (see useHistoricalFallback above).
  const tableRows = useHistoricalFallback
    ? (tableRowsHistorical ?? tableRowsLive)
    : tableRowsLive;


  // ============ Summary KPIs ============
  // In historical-fallback mode the warehouse stores two independent grains
  // (per-SKU and per-channel). For snapshots written before Mar 2 2026 the two
  // grains can disagree. We treat the per-channel grain (= channelSummary) as
  // canonical for page-level KPIs so cards ALWAYS match the channel summary
  // totals. The SKU table still renders the per-SKU rows that were saved; the
  // gap (if any) is surfaced via `historicalGrainGap` below.
  const isHistoricalView = useHistoricalFallback && tableRowsHistorical != null;

  const _skuTotProjected  = tableRows.reduce((s, r) => s + r.projected,  0);
  const _skuTotDispatched = tableRows.reduce((s, r) => s + r.dispatched, 0);
  const _skuTotSales      = tableRows.reduce((s, r) => s + r.sales,      0);
  const _chTotProjected   = channelSummary.reduce((s, r) => s + (r.projection || 0), 0);
  const _chTotDispatched  = channelSummary.reduce((s, r) => s + (r.dispatched || 0), 0);
  const _chTotSales       = channelSummary.reduce((s, r) => s + (r.sales || 0),       0);

  const totalProjected  = isHistoricalView ? _chTotProjected  : _skuTotProjected;
  const totalDispatched = isHistoricalView ? _chTotDispatched : _skuTotDispatched;
  const totalSales      = isHistoricalView ? _chTotSales      : _skuTotSales;
  const overallPct = totalProjected > 0 ? (totalDispatched / totalProjected) * 100 : null;
  const overallSalesPct = totalProjected > 0 ? (totalSales / totalProjected) * 100 : null;

  // Gap between the two grains — surfaced as a small note in the historical
  // banner when warehouse legacy data is internally inconsistent.
  const historicalGrainGap = isHistoricalView && (
    _skuTotProjected  !== _chTotProjected  ||
    _skuTotDispatched !== _chTotDispatched ||
    _skuTotSales      !== _chTotSales
  );

  // ============ Channel Summary sorting (independent per table) ============
  // Tri-state: asc → desc → cleared (returns to natural channel-filter order).
  const [t1SortKey, setT1SortKey] = useState(null);
  const [t1SortDir, setT1SortDir] = useState('asc');
  const [t2SortKey, setT2SortKey] = useState(null);
  const [t2SortDir, setT2SortDir] = useState('asc');

  const makeToggle = (key, dir, setKey, setDir) => (k) => {
    if (key !== k) { setKey(k); setDir('asc'); return; }
    if (dir === 'asc') { setDir('desc'); return; }
    setKey(null); setDir('asc');
  };
  const toggleT1 = makeToggle(t1SortKey, t1SortDir, setT1SortKey, setT1SortDir);
  const toggleT2 = makeToggle(t2SortKey, t2SortDir, setT2SortKey, setT2SortDir);

  const sortRows = (rows, key, dir) => {
    if (!key) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }
      const sa = av.toString().toLowerCase();
      const sb = bv.toString().toLowerCase();
      if (sa < sb) return dir === 'asc' ? -1 : 1;
      if (sa > sb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  };
  const t1Rows = useMemo(() => sortRows(channelSummaryAugmented, t1SortKey, t1SortDir), [channelSummaryAugmented, t1SortKey, t1SortDir]);
  const t2Rows = useMemo(() => sortRows(channelSummary, t2SortKey, t2SortDir), [channelSummary, t2SortKey, t2SortDir]);

  // ============ Auto-sync to MongoDB historical warehouse ============
  // Only fire in LIVE mode (current/future range). Past ranges are served
  // from MongoDB and we never overwrite today's auto-sync with historical
  // playback data.
  const warehouseSyncedKeyRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (isPastRange) return;
    if (tableRows.length === 0 && channelSummary.length === 0) return;
    const key = `${todayISODate()}|${startDate}-${endDate}|${tableRows.length}|${channelSummary.length}`;
    if (warehouseSyncedKeyRef.current === key) return;
    warehouseSyncedKeyRef.current = key;

    const today = todayISODate();
    const projDate = parseLocalDate(startDate);
    const sourceMonth = projDate ? projDate.getMonth() : new Date().getMonth();
    const sourceYear  = projDate ? projDate.getFullYear() : new Date().getFullYear();

    const records = [];
    // Per-SKU records (channel sentinel "__TOTAL__")
    tableRows.forEach(r => {
      const projected  = Math.round(Number(r.projected)  || 0);
      const sales      = Math.round(Number(r.sales)      || 0);
      const dispatched = Math.round(Number(r.dispatched) || 0);
      if (projected === 0 && sales === 0 && dispatched === 0) return;
      records.push({
        date:             today,
        channel:          '__TOTAL__',
        sku:              r.sku,
        asin:             r.asin  || '',
        brand:            r.brand || '',
        projected_units:  projected,
        sales_units:      sales,
        dispatched_units: dispatched,
        source:           'consumption',
        source_month:     sourceMonth,
        source_year:      sourceYear,
      });
    });
    // Per-channel records (sku sentinel "__TOTAL__")
    channelSummary.forEach(c => {
      const projected  = Math.round(Number(c.projection) || 0);
      const sales      = Math.round(Number(c.sales)      || 0);
      const dispatched = Math.round(Number(c.dispatched) || 0);
      if (projected === 0 && sales === 0 && dispatched === 0) return;
      records.push({
        date:             today,
        channel:          c.channel,
        sku:              '__TOTAL__',
        projected_units:  projected,
        sales_units:      sales,
        dispatched_units: dispatched,
        source:           'consumption',
        source_month:     sourceMonth,
        source_year:      sourceYear,
      });
    });
    pushWarehouseRecords(records);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tableRows, channelSummary, startDate, endDate]);


  // ============ Historical Snapshots (Projections vs Consumption) ============
  // Mirrors the rolling-projections snapshot flow: capture the exact-as-visible
  // channel summary + SKU table at the time of save, dedup via server-side
  // hash, auto-version per (projection_month, projection_year).
  //
  // The "projection month" here is taken from the start date — anchoring to
  // whatever month the user has filtered consumption against.
  const projectionMonth = useMemo(() => {
    const d = parseLocalDate(startDate);
    return d ? d.getMonth() : new Date().getMonth();
  }, [startDate]);
  const projectionYear = useMemo(() => {
    const d = parseLocalDate(startDate);
    return d ? d.getFullYear() : new Date().getFullYear();
  }, [startDate]);

  const [snapshotsList, setSnapshotsList] = useState([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotsDialogOpen, setSnapshotsDialogOpen] = useState(false);
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(
    () => sessionStorage.getItem('editor_unlocked') === '1'
  );
  const [adminPwInMem, setAdminPwInMem] = useState(
    () => sessionStorage.getItem('editor_pw') || ''
  );
  const [adminPwDialogOpen, setAdminPwDialogOpen] = useState(false);
  const [adminPwInput, setAdminPwInput] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
  });

  // Build the exact-as-visible payload. `channels` mirrors what the Channel
  // Summary table shows (including dispPct / salePct). `rows` mirrors the SKU
  // table, projected/dispatched/sales/pct/salesPct.
  const buildSnapshotPayload = () => ({
    projection_month: projectionMonth,
    projection_year:  projectionYear,
    start_date:       startDate,
    end_date:         endDate,
    channels: channelSummary.map(c => ({
      channel:    c.channel,
      projection: Math.round(c.projection),
      dispatched: Math.round(c.dispatched),
      sales:      Math.round(c.sales),
      dispPct:    c.dispPct  != null ? Number(c.dispPct.toFixed(2))  : null,
      salePct:    c.salePct  != null ? Number(c.salePct.toFixed(2))  : null,
    })),
    rows: tableRows.map(r => ({
      asin:       r.asin || '',
      brand:      r.brand || '',
      sku:        r.sku,
      projected:  Math.round(r.projected),
      dispatched: Math.round(r.dispatched),
      sales:      Math.round(r.sales),
      pct:        r.pct      != null ? Number(r.pct.toFixed(2))      : null,
      salesPct:   r.salesPct != null ? Number(r.salesPct.toFixed(2)) : null,
      unmapped:   !!r.unmapped,
    })),
  });

  const handleSaveSnapshot = async () => {
    if (tableRows.length === 0 && channelSummary.length === 0) {
      toast.error('Nothing to snapshot');
      return;
    }
    setSavingSnapshot(true);
    try {
      const res = await axios.post(
        `${API}/projections-consumption/snapshots`,
        buildSnapshotPayload(),
        { headers: authHeaders() },
      );
      const v = res.data?.snapshot?.version;
      toast.success(v ? `Snapshot saved · v${v}` : 'Historical snapshot saved');
      fetchSnapshotsList();
    } catch (e) {
      if (e.response?.status === 409) toast.info('Snapshot already exists.');
      else toast.error(e.response?.data?.detail || 'Failed to save snapshot');
    } finally {
      setSavingSnapshot(false);
    }
  };

  const fetchSnapshotsList = async () => {
    setSnapshotsLoading(true);
    try {
      const res = await axios.get(`${API}/projections-consumption/snapshots`, { headers: authHeaders() });
      setSnapshotsList(res.data.snapshots || []);
    } catch (e) {
      toast.error('Failed to load snapshots');
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const handleOpenSnapshotsDialog = () => {
    setSnapshotsDialogOpen(true);
    fetchSnapshotsList();
  };

  const handleViewSnapshot = async (id) => {
    try {
      const res = await axios.get(`${API}/projections-consumption/snapshots/${id}`, { headers: authHeaders() });
      setViewingSnapshot(res.data);
    } catch (e) {
      toast.error('Failed to load snapshot');
    }
  };

  // Excel: 3 sheets — Channel Summary · SKU Detail · Snapshot Info.
  const handleDownloadSnapshot = (snap) => {
    if (!snap) return;
    const wb = XLSX.utils.book_new();
    const chHeader = ['Channel', 'Projected', 'Dispatched', 'Sales', '% Dispatched vs Proj', '% Sales vs Proj'];
    const chAoa = [chHeader, ...(snap.channels || []).map(c => [
      c.channel, c.projection, c.dispatched, c.sales,
      c.dispPct != null ? `${c.dispPct.toFixed(1)}%` : '—',
      c.salePct != null ? `${c.salePct.toFixed(1)}%` : '—',
    ])];
    const wsCh = XLSX.utils.aoa_to_sheet(chAoa);
    wsCh['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsCh, 'Channel Summary');

    const skuHeader = ['ASIN', 'Brand', 'SKU', 'Projected', 'Dispatched', 'Sales', '% Dispatched vs Proj', '% Sales vs Proj', 'Unmapped'];
    const skuAoa = [skuHeader, ...(snap.rows || []).map(r => [
      r.asin || '', r.brand || '', r.sku || '',
      r.projected, r.dispatched, r.sales,
      r.pct      != null ? `${r.pct.toFixed(1)}%`      : '—',
      r.salesPct != null ? `${r.salesPct.toFixed(1)}%` : '—',
      r.unmapped ? 'Yes' : '',
    ])];
    const wsSku = XLSX.utils.aoa_to_sheet(skuAoa);
    wsSku['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 36 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsSku, 'SKU Detail');

    const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const meta = [
      ['Projection Month', MONTHS[snap.projection_month]],
      ['Projection Year', snap.projection_year],
      ['Version', snap.version ?? 1],
      ['Date Range', `${snap.start_date} → ${snap.end_date}`],
      ['Saved At', snap.save_timestamp],
      ['Saved By (user_id)', snap.user_id || '—'],
      ['Channel rows', snap.channel_count],
      ['SKU rows', snap.row_count],
    ];
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    wsMeta['!cols'] = [{ wch: 22 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, 'Snapshot Info');
    XLSX.writeFile(wb, `ProjectionsConsumption_Snapshot_${snap.save_date}_${MONTHS[snap.projection_month]}${snap.projection_year}_v${snap.version ?? 1}.xlsx`);
    toast.success('Snapshot downloaded');
  };

  const handleAttemptDelete = (id) => {
    if (!adminUnlocked) {
      setPendingDeleteId(id);
      setAdminPwDialogOpen(true);
      return;
    }
    runDelete(id, adminPwInMem);
  };

  const runDelete = async (id, password) => {
    try {
      await axios.delete(`${API}/projections-consumption/snapshots/${id}`, {
        headers: { ...authHeaders(), 'X-Editor-Password': password },
      });
      toast.success('Snapshot deleted');
      setSnapshotsList(prev => prev.filter(s => s.id !== id));
      if (viewingSnapshot?.id === id) setViewingSnapshot(null);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem('editor_unlocked');
        sessionStorage.removeItem('editor_pw');
        setAdminUnlocked(false);
        setAdminPwInMem('');
        toast.error('Admin password expired — please re-enter');
        setAdminPwDialogOpen(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to delete snapshot');
      }
    }
  };

  const handleAdminUnlockSubmit = async () => {
    if (!adminPwInput.trim()) return;
    try {
      await axios.post(`${API}/auth/editor-unlock`,
        { password: adminPwInput }, { headers: authHeaders() });
      sessionStorage.setItem('editor_unlocked', '1');
      sessionStorage.setItem('editor_pw', adminPwInput);
      setAdminUnlocked(true);
      setAdminPwInMem(adminPwInput);
      setAdminPwDialogOpen(false);
      const idToDelete = pendingDeleteId;
      const pw = adminPwInput;
      setAdminPwInput('');
      setPendingDeleteId(null);
      if (idToDelete) runDelete(idToDelete, pw);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid admin password');
    }
  };

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">Projections vs Consumption</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Source: {projectionSheets.length ? projectionSheets.join(' + ') : (projectionSheet || 'monthly projection sheet')} · Blinkit Planned units · MTD Sales</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSaveSnapshot}
              disabled={loading || savingSnapshot || (tableRows.length === 0 && channelSummary.length === 0)}
              data-testid="projections-save-snapshot-btn"
            >
              <Archive className="mr-2" size={16} />
              {savingSnapshot ? 'Saving…' : 'Save Historical Data'}
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenSnapshotsDialog}
              data-testid="projections-open-snapshots-btn"
            >
              <Eye className="mr-2" size={16} />
              Snapshots
            </Button>
            <Button onClick={fetchAll} disabled={loading} data-testid="projections-refresh-button">
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-4 md:p-8 space-y-6">
        {/* Row 1: Filters — All reset | Channel | Brand | SKU */}
        <section
          className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr] gap-3 items-end"
          data-testid="projections-filters-row"
        >
          <div className="flex flex-col">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block invisible">&nbsp;</Label>
            <Button
              type="button"
              variant="outline"
              data-testid="projections-filters-all-reset"
              onClick={() => { setSelectedChannels([]); setSelectedBrands([]); setSelectedSkus([]); }}
              className="h-9"
              title="Clear all filters"
            >
              All
            </Button>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Channel Filter</Label>
            <MultiSelect
              testId="projections-channel-filter"
              options={CHANNEL_OPTIONS.map(o => ({ value: o.value, label: o.value }))}
              selected={selectedChannels}
              onChange={setSelectedChannels}
              placeholder="All Channels"
              emptyLabel="All Channels"
              searchable
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Brand Filter</Label>
            <MultiSelect
              testId="projections-brand-filter"
              options={brandOptions}
              selected={selectedBrands}
              onChange={setSelectedBrands}
              placeholder="All Brands"
              emptyLabel="All Brands"
              searchable
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">SKU Filter</Label>
            <MultiSelect
              testId="projections-sku-filter"
              options={skuOptions}
              selected={selectedSkus}
              onChange={setSelectedSkus}
              placeholder="All SKUs"
              emptyLabel="All SKUs"
              searchable
            />
          </div>
        </section>

        {/* Date range filter */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end" data-testid="projections-date-row">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="projections-start-date"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="projections-end-date"
            />
          </div>
        </section>

        {/* Data source banner — always shows the LIVE Google Sheets aggregation
            unless live returns zero records for the selected range AND the
            MongoDB warehouse has data for that range to fall back on. */}
        <div
          className={`text-xs px-3 py-2 rounded border flex flex-wrap items-center gap-x-2 gap-y-1 ${
            useHistoricalFallback
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-sky-50 border-sky-200 text-sky-800'
          }`}
          data-testid="projections-source-banner"
        >
          {useHistoricalFallback ? (
            <>
              <span className="font-semibold">Historical fallback · MongoDB</span>
              <span className="opacity-80">
                · live sheets returned 0 records · anchor date {warehouseAnchorDate || endDate} · {channelSummaryHistorical?.length || 0} channels · {tableRowsHistorical?.length || 0} SKUs
              </span>
              {historicalGrainGap && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] uppercase tracking-wider font-semibold">
                  Legacy data · SKU grain ≠ channel grain
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-semibold">Live mode · Google Sheets</span>
              <span className="opacity-80">
                · {unifiedFacts.counts?.projectionRowsKept || 0} projection rows · {unifiedFacts.counts?.dispatchRowsKept || 0} dispatch records · {unifiedFacts.counts?.salesRowsKept || 0} sales records in range
              </span>
              {isPastRange && warehouseLoading && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider font-semibold">
                  Warehouse loading…
                </span>
              )}
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground" data-testid="projections-summary-text">
          Showing <span className="font-semibold text-wv-ink">{tableRows.length}</span> SKU{tableRows.length === 1 ? '' : 's'}
          {' · '}
          <span className="font-semibold text-wv-ink">{formatInt(totalDispatched)}</span> dispatched ·{' '}
          <span className="font-semibold text-wv-ink">{formatInt(totalSales)}</span> sales units between {startDate} and {endDate}
        </p>

        {/* Summary cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="kpi-projected">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Projected Units</p>
                <Target size={18} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tracking-tight text-wv-ink tabular-nums">{formatInt(totalProjected)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="kpi-projected-subtitle">
                {selectedChannels.length === 0
                  ? `All channels · ${projectionSheets.length ? projectionSheets.join(' + ') : (projectionSheet || 'projection sheet')}`
                  : `${selectedChannels.length} channel${selectedChannels.length === 1 ? '' : 's'} · ${projectionSheets.length ? projectionSheets.join(' + ') : (projectionSheet || 'projection sheet')}`}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-dispatched">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Dispatched Units</p>
                <ShoppingBag size={18} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tracking-tight text-wv-ink tabular-nums">{formatInt(totalDispatched)}</p>
              <div className="flex items-center gap-2 mt-2" data-testid="kpi-dispatched-pct">
                <Percent size={12} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Overall %&nbsp;Dispatched:&nbsp;
                  <span className={`tabular-nums font-semibold inline-block px-1.5 rounded ${overallPct != null ? pctTone(overallPct) : ''}`}>
                    {overallPct != null ? `${overallPct.toFixed(1)}%` : '—'}
                  </span>
                </p>
              </div>
              <p className="text-[10px] text-muted-foreground/80 mt-1" data-testid="kpi-dispatched-note">
                {!anyDispatchInScope
                  ? 'No dispatched source for selected channel(s)'
                  : pendingDispatchChannels.length === 0
                  ? `Source: ${activeDispatchSources.length} channel${activeDispatchSources.length === 1 ? '' : 's'} synced`
                  : `Synced: ${activeDispatchSources.length} channel${activeDispatchSources.length === 1 ? '' : 's'} · pending: ${pendingDispatchChannels.join(', ')}`}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-sales">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Sales Units</p>
                <TrendingUp size={18} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tracking-tight text-wv-ink tabular-nums">{formatInt(totalSales)}</p>
              <div className="flex items-center gap-2 mt-2" data-testid="kpi-sales-pct">
                <Percent size={12} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Sales % vs Projection:&nbsp;
                  <span className={`tabular-nums font-semibold inline-block px-1.5 rounded ${overallSalesPct != null ? pctTone(overallSalesPct) : ''}`}>
                    {overallSalesPct != null ? `${overallSalesPct.toFixed(1)}%` : '—'}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Channel summary — two side-by-side tables. */}
        <section data-testid="channel-summary-section" className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Table 1: Projection vs Sales (with monthly pacing context) */}
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/30">
              <h2 className="text-sm font-semibold text-wv-ink">Channel Summary — Projection vs Sales</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Sales pacing against monthly projection · Ideal % = {idealSalesPct.toFixed(1)}% (selected {idealDaysInfo.days}/{idealDaysInfo.total} days)
              </p>
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm" data-testid="channel-summary-table-1">
                <thead className="sticky top-0 z-10 bg-[#2EB4AC]">
                  <tr>
                    {[
                      { key: 'channel',     label: 'Channel',          align: 'left' },
                      { key: 'projection',  label: 'Projection Units', align: 'right' },
                      { key: 'sales',       label: 'Sales Units',      align: 'right' },
                      { key: 'salePct',     label: 'Sales %',          align: 'right' },
                      { key: 'idealPct',    label: 'Ideal Sales %',    align: 'right' },
                      { key: 'variancePct', label: 'Variance %',       align: 'right' },
                    ].map(h => {
                      const active = t1SortKey === h.key;
                      const Icon = active ? (t1SortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                      return (
                        <th
                          key={h.key}
                          onClick={() => toggleT1(h.key)}
                          data-testid={`channel-summary-t1-sort-${h.key}`}
                          className="p-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/95 cursor-pointer select-none hover:text-white"
                          style={{ textAlign: h.align }}
                        >
                          <span className={`inline-flex items-center gap-1 ${h.align === 'right' ? 'flex-row-reverse' : ''}`}>
                            {h.label}
                            <Icon size={11} className={active ? 'text-white' : 'text-white/60'} />
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {t1Rows.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-[13px] text-muted-foreground">No data</td></tr>
                  ) : t1Rows.map((r, idx) => (
                    <tr
                      key={r.channel}
                      className={`${idx % 2 === 1 ? 'bg-muted/15' : 'bg-card'} hover:bg-primary/5 transition-colors`}
                      data-testid={`channel-summary-t1-row-${r.channel.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <td className="text-left p-2 text-[13px] text-wv-body font-medium">{r.channel}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums text-wv-ink">{formatInt(r.projection)}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums text-wv-ink">{formatInt(r.sales)}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums">
                        {r.salePct == null
                          ? <span className="text-[#64748B]">—</span>
                          : <span className={`inline-block px-2 py-0.5 rounded font-medium ${pctTone(r.salePct)}`}>{r.salePct.toFixed(1)}%</span>}
                      </td>
                      <td className="text-right p-2 text-[13px] tabular-nums text-wv-body">{r.idealPct.toFixed(1)}%</td>
                      <td className="text-right p-2 text-[13px] tabular-nums">
                        {r.variancePct == null
                          ? <span className="text-[#64748B]">—</span>
                          : <span className={`inline-block px-2 py-0.5 rounded font-medium ${r.variancePct >= 0 ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#FEE2E2] text-[#B91C1C]'}`}>
                              {r.variancePct >= 0 ? '+' : ''}{r.variancePct.toFixed(1)}%
                            </span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {t1Rows.length > 0 && (
                  <tfoot>
                    {(() => {
                      const p  = t1Rows.reduce((s, r) => s + r.projection, 0);
                      const sl = t1Rows.reduce((s, r) => s + r.sales, 0);
                      const sp = p > 0 ? (sl / p) * 100 : null;
                      const vp = sp == null ? null : sp - idealSalesPct;
                      return (
                        <tr className="bg-[#0F0F0F] text-white font-semibold" data-testid="channel-summary-t1-totals">
                          <td className="text-left p-2 text-[13px] text-white">Total</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{formatInt(p)}</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{formatInt(sl)}</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{sp != null ? `${sp.toFixed(1)}%` : '—'}</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{idealSalesPct.toFixed(1)}%</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{vp == null ? '—' : `${vp >= 0 ? '+' : ''}${vp.toFixed(1)}%`}</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Table 2: Dispatched vs Sales (sell-through) */}
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/30">
              <h2 className="text-sm font-semibold text-wv-ink">Channel Summary — Dispatched vs Sales</h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Sales % = Dispatched / Sales · filters applied
              </p>
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm" data-testid="channel-summary-table-2">
                <thead className="sticky top-0 z-10 bg-[#2EB4AC]">
                  <tr>
                    {[
                      { key: 'channel',       label: 'Channel',                   align: 'left' },
                      { key: 'sales',         label: 'Sales Units',               align: 'right' },
                      { key: 'dispatched',    label: 'Dispatched Units',          align: 'right' },
                      { key: 'dispSalesPct',  label: 'Sales % (Dispatch/Sales)',  align: 'right' },
                    ].map(h => {
                      const active = t2SortKey === h.key;
                      const Icon = active ? (t2SortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                      return (
                        <th
                          key={h.key}
                          onClick={() => toggleT2(h.key)}
                          data-testid={`channel-summary-t2-sort-${h.key}`}
                          className="p-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/95 cursor-pointer select-none hover:text-white"
                          style={{ textAlign: h.align }}
                        >
                          <span className={`inline-flex items-center gap-1 ${h.align === 'right' ? 'flex-row-reverse' : ''}`}>
                            {h.label}
                            <Icon size={11} className={active ? 'text-white' : 'text-white/60'} />
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {t2Rows.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-[13px] text-muted-foreground">No data</td></tr>
                  ) : t2Rows.map((r, idx) => (
                    <tr
                      key={r.channel}
                      className={`${idx % 2 === 1 ? 'bg-muted/15' : 'bg-card'} hover:bg-primary/5 transition-colors`}
                      data-testid={`channel-summary-t2-row-${r.channel.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <td className="text-left p-2 text-[13px] text-wv-body font-medium">{r.channel}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums text-wv-ink">{formatInt(r.sales)}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums text-wv-ink">{formatInt(r.dispatched)}</td>
                      <td className="text-right p-2 text-[13px] tabular-nums">
                        {r.sales <= 0 || r.dispSalesPct == null
                          ? <span className="text-[#64748B]">—</span>
                          : <span className={`inline-block px-2 py-0.5 rounded font-medium ${pctTone(r.dispSalesPct)}`}>{r.dispSalesPct.toFixed(1)}%</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {t2Rows.length > 0 && (
                  <tfoot>
                    {(() => {
                      const d  = t2Rows.reduce((s, r) => s + r.dispatched, 0);
                      const sl = t2Rows.reduce((s, r) => s + r.sales, 0);
                      // Totals row uses the same new formula: Dispatched / Sales.
                      // Falls back to "—" when sales = 0.
                      const dsp = sl > 0 ? (d / sl) * 100 : null;
                      return (
                        <tr className="bg-[#0F0F0F] text-white font-semibold" data-testid="channel-summary-t2-totals">
                          <td className="text-left p-2 text-[13px] text-white">Total</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{formatInt(sl)}</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{formatInt(d)}</td>
                          <td className="text-right p-2 text-[13px] tabular-nums text-white">{dsp != null ? `${dsp.toFixed(1)}%` : '—'}</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </section>

        {/* Projection × Dispatch × Sales table */}
        <section>
          <DataTable
            testId="projections-table"
            searchKeys={['sku', 'brand']}
            searchPlaceholder="Search SKU or brand…"
            emptyMessage="No data"
            maxHeight="560px"
            defaultPageSize={15}
            pageSizeOptions={[10, 15, 20, 100]}
            columns={[
              {
                key: 'sku',
                header: 'SKU',
                width: '34%',
                sortable: true,
                render: (r) => r.unmapped
                  ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FEE2E2] text-[#B91C1C]">Unmapped</span>
                      {r.sku}
                    </span>
                  )
                  : r.sku,
              },
              {
                key: 'brand',
                header: 'Brand',
                width: '12%',
                sortable: true,
                render: (r) => r.brand || <span className="text-[#64748B]">—</span>,
              },
              {
                key: 'projected',
                header: 'Projected Units',
                align: 'right',
                width: '13%',
                sortable: true,
                sortAccessor: (r) => r.projected,
                render: (r) => <span className="tabular-nums">{formatInt(r.projected)}</span>,
              },
              {
                key: 'dispatched',
                header: 'Dispatched Units',
                align: 'right',
                width: '13%',
                sortable: true,
                sortAccessor: (r) => r.dispatched,
                render: (r) => <span className="tabular-nums">{formatInt(r.dispatched)}</span>,
              },
              {
                key: 'pct',
                header: '% Dispatched (w.r.t Projections)',
                align: 'right',
                width: '12%',
                sortable: true,
                sortAccessor: (r) => r.pct ?? -1,
                render: (r) => r.pct == null
                  ? <span className="text-[#64748B]">—</span>
                  : <span className={`inline-block px-2 py-0.5 rounded tabular-nums font-medium ${pctTone(r.pct)}`}>{r.pct.toFixed(1)}%</span>,
              },
              {
                key: 'sales',
                header: 'Gross Sales Units',
                align: 'right',
                width: '14%',
                sortable: true,
                sortAccessor: (r) => r.sales,
                render: (r) => <span className="tabular-nums">{formatInt(r.sales)}</span>,
              },
              {
                key: 'salesPct',
                header: '% Sales (w.r.t Projections)',
                align: 'right',
                width: '12%',
                sortable: true,
                sortAccessor: (r) => r.salesPct ?? -1,
                render: (r) => r.salesPct == null
                  ? <span className="text-[#64748B]">—</span>
                  : <span className={`inline-block px-2 py-0.5 rounded tabular-nums font-medium ${pctTone(r.salesPct)}`}>{r.salesPct.toFixed(1)}%</span>,
              },
            ]}
            rows={tableRows.map(r => ({ ...r, __key: `${r.sku}-${r.unmapped}` }))}
            renderTopRow={(rows) => {
              const p = rows.reduce((s, r) => s + r.projected, 0);
              const d = rows.reduce((s, r) => s + r.dispatched, 0);
              const sl = rows.reduce((s, r) => s + r.sales, 0);
              const ov = p > 0 ? (d / p) * 100 : null;
              const sp = p > 0 ? (sl / p) * 100 : null;
              return (
                <>
                  <td className="px-3 py-2 text-[13px] font-semibold text-white" colSpan={2} style={{ whiteSpace: 'nowrap' }}>Total</td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-projected">
                    {formatInt(p)}
                  </td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-dispatched">
                    {formatInt(d)}
                  </td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-pct">
                    {ov != null ? `${ov.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-sales">
                    {formatInt(sl)}
                  </td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-sales-pct">
                    {sp != null ? `${sp.toFixed(1)}%` : '—'}
                  </td>
                </>
              );
            }}
          />
        </section>
      </main>

      {/* ============ Snapshots dialog (list) ============ */}
      <Dialog open={snapshotsDialogOpen} onOpenChange={setSnapshotsDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="projections-snapshots-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={18} /> Historical Snapshots · Projections vs Consumption
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {snapshotsLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : snapshotsList.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="projections-snapshots-empty">
                No snapshots yet. Click <strong>Save Historical Data</strong> to capture the current view.
              </p>
            ) : (
              <table className="w-full border-collapse text-sm" data-testid="projections-snapshots-table">
                <thead className="bg-primary text-primary-foreground sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs uppercase tracking-widest">Projection</th>
                    <th className="text-left p-2 text-xs uppercase tracking-widest">Date Range</th>
                    <th className="text-left p-2 text-xs uppercase tracking-widest">Saved At</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest">Channels</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest">SKUs</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshotsList.map((s, i) => (
                    <tr key={s.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b border-border`} data-testid={`projections-snapshot-row-${s.id}`}>
                      <td className="p-2 text-xs text-wv-ink">
                        <span className="font-medium">{MONTHS[s.projection_month]} {s.projection_year}</span>
                        {s.version != null && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary"
                            data-testid={`projections-snapshot-version-${s.id}`}>
                            v{s.version}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-[11px] text-wv-body">{s.start_date} → {s.end_date}</td>
                      <td className="p-2 text-xs text-wv-body">{new Date(s.save_timestamp).toLocaleString()}</td>
                      <td className="p-2 text-xs text-right tabular-nums">{s.channel_count}</td>
                      <td className="p-2 text-xs text-right tabular-nums">{s.row_count}</td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="h-7 px-2"
                            onClick={() => handleViewSnapshot(s.id)}
                            data-testid={`projections-snapshot-view-${s.id}`}
                          >
                            <Eye size={12} className="mr-1" />View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2"
                            onClick={async () => {
                              const r = await axios.get(`${API}/projections-consumption/snapshots/${s.id}`, { headers: authHeaders() });
                              handleDownloadSnapshot(r.data);
                            }}
                            data-testid={`projections-snapshot-download-${s.id}`}
                          >
                            <Download size={12} className="mr-1" />Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 border-rose-500 text-rose-600 hover:bg-rose-50"
                            onClick={() => handleAttemptDelete(s.id)}
                            data-testid={`projections-snapshot-delete-${s.id}`}
                            title="Admin only"
                          >
                            <Trash2 size={12} className="mr-1" />Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSnapshotsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshot viewer — channel summary + SKU detail, frozen from Mongo. */}
      <Dialog open={!!viewingSnapshot} onOpenChange={(o) => { if (!o) setViewingSnapshot(null); }}>
        <DialogContent className="max-w-6xl" data-testid="projections-snapshot-viewer">
          <DialogHeader>
            <DialogTitle>
              {viewingSnapshot
                ? `Snapshot · ${MONTHS[viewingSnapshot.projection_month]} ${viewingSnapshot.projection_year}${viewingSnapshot.version != null ? ` · v${viewingSnapshot.version}` : ''}`
                : 'Snapshot'}
            </DialogTitle>
          </DialogHeader>
          {viewingSnapshot && (
            <div className="space-y-4 max-h-[70vh] overflow-auto">
              <div className="text-xs text-muted-foreground">
                Saved {new Date(viewingSnapshot.save_timestamp).toLocaleString()} ·
                Date range {viewingSnapshot.start_date} → {viewingSnapshot.end_date} ·
                User <code className="text-[10px] bg-slate-100 px-1 rounded">{viewingSnapshot.user_id}</code>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-wv-ink mb-2">Channel Summary</h3>
                <div className="border border-border rounded overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-primary text-primary-foreground">
                      <tr>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">Channel</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Projected</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Dispatched</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Sales</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">% Disp/Proj</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">% Sale/Proj</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewingSnapshot.channels || []).map((c, i) => (
                        <tr key={c.channel} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-2 text-wv-ink">{c.channel}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(c.projection)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(c.dispatched)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(c.sales)}</td>
                          <td className="p-2 text-right tabular-nums">{c.dispPct != null ? `${c.dispPct.toFixed(1)}%` : '—'}</td>
                          <td className="p-2 text-right tabular-nums">{c.salePct != null ? `${c.salePct.toFixed(1)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-wv-ink mb-2">SKU Detail ({viewingSnapshot.row_count})</h3>
                <div className="border border-border rounded overflow-auto" style={{ maxHeight: '42vh' }}>
                  <table className="w-full text-xs">
                    <thead className="bg-primary text-primary-foreground sticky top-0">
                      <tr>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">ASIN</th>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">Brand</th>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">SKU</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Projected</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Dispatched</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Sales</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">% Disp/Proj</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">% Sale/Proj</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewingSnapshot.rows || []).map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-2 text-wv-ink whitespace-nowrap">{r.asin || '—'}</td>
                          <td className="p-2 text-wv-body whitespace-nowrap">{r.brand || '—'}</td>
                          <td className="p-2 text-wv-ink">{r.sku}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(r.projected)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(r.dispatched)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(r.sales)}</td>
                          <td className="p-2 text-right tabular-nums">{r.pct      != null ? `${r.pct.toFixed(1)}%`      : '—'}</td>
                          <td className="p-2 text-right tabular-nums">{r.salesPct != null ? `${r.salesPct.toFixed(1)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownloadSnapshot(viewingSnapshot)} disabled={!viewingSnapshot}>
              <Download size={14} className="mr-2" /> Download Excel
            </Button>
            <Button variant="outline" onClick={() => setViewingSnapshot(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin password gate */}
      <Dialog open={adminPwDialogOpen} onOpenChange={(o) => { if (!o) { setAdminPwDialogOpen(false); setAdminPwInput(''); setPendingDeleteId(null); } }}>
        <DialogContent className="max-w-sm" data-testid="projections-admin-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LockKeyhole size={18} /> Admin Password Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Admin Password</Label>
            <Input
              type="password" autoFocus value={adminPwInput}
              onChange={(e) => setAdminPwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdminUnlockSubmit(); }}
              data-testid="projections-admin-pw-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setAdminPwDialogOpen(false); setAdminPwInput(''); setPendingDeleteId(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleAdminUnlockSubmit} disabled={!adminPwInput.trim()} data-testid="projections-admin-pw-submit">Unlock & Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
